import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  AppBar,
  Toolbar,
  InputAdornment,
  Chip,
  Alert
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  CalendarToday as CalendarIcon,
  Close as CloseIcon
} from '@mui/icons-material';

const AiSprintRetro = ({ collapsed, isModal = false, onClose }) => {
  const [summaries, setSummaries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [timeRange, setTimeRange] = useState('Show All Time');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedSummary, setSelectedSummary] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState('');

  const fetchSprintUpdates = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(process.env.REACT_APP_CLIENT_SPRINTUPDATES, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({})
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      const bodyData = JSON.parse(data.body);
      
      if (bodyData.success && bodyData.data.consolidated) {
        // Create a summary entry with current timestamp
        const summaryEntry = {
          id: Date.now(),
          timestamp: new Date().toISOString(),
          summary: bodyData.data.consolidated,
          metadata: bodyData.data.metadata,
          jiraData: bodyData.data.jira
        };
        setSummaries([summaryEntry]);
      } else {
        throw new Error('Invalid data format received');
      }
    } catch (error) {
      console.error('Error fetching sprint updates:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSprintUpdates();
  }, []);

  const formatDateTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }) + ', ' + date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const viewSummary = (summary) => {
    setSelectedSummary(summary);
    setShowModal(true);
  };

  const closeSummary = () => {
    setShowModal(false);
    setSelectedSummary(null);
  };

  const filteredSummaries = summaries.filter(summary =>
    summary.summary.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Modal version - simplified layout
  if (isModal) {
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Modal Header */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          p: 2,
          borderBottom: '1px solid #e0e0e0'
        }}>
          <Typography variant="h5" sx={{ color: '#1976d2', fontWeight: 600 }}>
            AI Sprint Retro
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton
              onClick={fetchSprintUpdates}
              disabled={loading}
              sx={{ bgcolor: '#f0f0f0', '&:hover': { bgcolor: '#e0e0e0' } }}
            >
              {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
            </IconButton>
            {onClose && (
              <IconButton onClick={onClose}>
                <CloseIcon />
              </IconButton>
            )}
          </Box>
        </Box>

        {/* Modal Content */}
        <Box sx={{ flex: 1, p: 2, overflow: 'auto' }}>
          {/* Filters */}
          <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField
                placeholder="Search in summaries..."
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 250 }}
              />
              
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Time Range</InputLabel>
                <Select
                  value={timeRange}
                  label="Time Range"
                  onChange={(e) => setTimeRange(e.target.value)}
                >
                  <MenuItem value="Show All Time">All Time</MenuItem>
                  <MenuItem value="Last 7 days">Last 7 days</MenuItem>
                  <MenuItem value="Last 30 days">Last 30 days</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Paper>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Summaries Table */}
          <TableContainer component={Paper} elevation={1} sx={{ maxHeight: '400px' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ bgcolor: '#1976d2', color: 'white', fontWeight: 600 }}>
                    Team Member
                  </TableCell>
                  <TableCell sx={{ bgcolor: '#1976d2', color: 'white', fontWeight: 600 }}>
                    Date & Time
                  </TableCell>
                  <TableCell sx={{ bgcolor: '#1976d2', color: 'white', fontWeight: 600 }}>
                    Summary
                  </TableCell>
                  <TableCell sx={{ bgcolor: '#1976d2', color: 'white', fontWeight: 600, width: 100 }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && summaries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ textAlign: 'center', py: 4 }}>
                      <CircularProgress />
                      <Typography variant="body2" sx={{ mt: 2 }}>
                        Loading sprint updates...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : filteredSummaries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        No summaries found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredSummaries.map((summary) => (
                    <TableRow key={summary.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip 
                            label="AI" 
                            size="small" 
                            sx={{ 
                              bgcolor: '#e3f2fd',
                              color: '#1976d2',
                              fontWeight: 600,
                              fontSize: '0.75rem'
                            }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDateTime(summary.timestamp)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {truncateText(summary.summary)}
                        </Typography>
                        {summary.metadata && (
                          <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Chip 
                              label={`${summary.metadata.total_sprints} Sprints`} 
                              size="small" 
                              color="primary" 
                              variant="outlined"
                            />
                            <Chip 
                              label={`${summary.metadata.total_prs} PRs`} 
                              size="small" 
                              color="secondary" 
                              variant="outlined"
                            />
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          color="primary"
                          onClick={() => viewSummary(summary)}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Summary Detail Modal */}
        <Dialog
          open={showModal}
          onClose={closeSummary}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: { maxHeight: '80vh' }
          }}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">
              Sprint Summary Details
            </Typography>
            <IconButton onClick={closeSummary} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            {selectedSummary && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                  Generated: {formatDateTime(selectedSummary.timestamp)}
                </Typography>
                
                {selectedSummary.metadata && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Overview</Typography>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                      <Chip 
                        label={`Total Sprints: ${selectedSummary.metadata.total_sprints}`} 
                        color="primary"
                      />
                      <Chip 
                        label={`Active Sprints: ${selectedSummary.metadata.active_sprints}`} 
                        color="success"
                      />
                      <Chip 
                        label={`Total PRs: ${selectedSummary.metadata.total_prs}`} 
                        color="secondary"
                      />
                      <Chip 
                        label={`Contributors: ${selectedSummary.metadata.total_github_contributors}`} 
                        color="info"
                      />
                    </Box>
                  </Box>
                )}
                
                <Typography variant="h6" sx={{ mb: 2 }}>Detailed Summary</Typography>
                <Typography
                  variant="body1"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    lineHeight: 1.6,
                    color: 'text.primary'
                  }}
                >
                  {selectedSummary.summary}
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeSummary} variant="outlined">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // Original full-page version
  return (
    <Box sx={{ 
      flexGrow: 1, 
      minHeight: '100vh'
    }}>
      {/* Header */}
      <AppBar position="static" sx={{ 
        bgcolor: 'white', 
        boxShadow: 1,
        marginLeft: collapsed ? "60px" : "240px",
        width: collapsed ? "calc(100vw - 60px)" : "calc(100vw - 240px)",
        transition: 'margin-left 0.3s ease, width 0.3s ease'
      }}>
        <Toolbar>
          <Typography variant="h5" component="h1" sx={{ flexGrow: 1, color: '#1976d2', fontWeight: 600 }}>
            Ai Sprint Retro
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton
              onClick={fetchSprintUpdates}
              disabled={loading}
              sx={{ bgcolor: '#f0f0f0', '&:hover': { bgcolor: '#e0e0e0' } }}
            >
              {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      <Container sx={{ 
        mt: 3,
        ml: collapsed ? "60px" : "240px",
        mr: 0,
        maxWidth: 'none !important',
        width: collapsed ? "calc(100vw - 80px)" : "calc(100vw - 260px)",
        transition: 'margin-left 0.3s ease, width 0.3s ease',
        px: 2
      }}>
        {/* Rest of the original content remains the same */}
        {/* Filters */}
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              placeholder="Search in summaries..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 300 }}
            />
            
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Quick Time Range</InputLabel>
              <Select
                value={timeRange}
                label="Quick Time Range"
                onChange={(e) => setTimeRange(e.target.value)}
              >
                <MenuItem value="Show All Time">Show All Time</MenuItem>
                <MenuItem value="Last 7 days">Last 7 days</MenuItem>
                <MenuItem value="Last 30 days">Last 30 days</MenuItem>
                <MenuItem value="Last 90 days">Last 90 days</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Start Date"
              type="date"
              size="small"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <CalendarIcon />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              label="End Date"
              type="date"
              size="small"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <CalendarIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Paper>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Summaries Table */}
        <TableContainer component={Paper} elevation={1}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#1976d2' }}>
                <TableCell sx={{ color: 'white', fontWeight: 600, py: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarIcon />
                    Date & Time
                  </Box>
                </TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600, py: 2 }}>
                  Summary
                </TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600, py: 2, width: 100 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && summaries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} sx={{ textAlign: 'center', py: 4 }}>
                    <CircularProgress />
                    <Typography variant="body2" sx={{ mt: 2 }}>
                      Loading sprint updates...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filteredSummaries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      No summaries found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredSummaries.map((summary) => (
                  <TableRow key={summary.id} hover>
                    <TableCell sx={{ py: 2 }}>
                      <Typography variant="body2" color="text.primary">
                        {formatDateTime(summary.timestamp)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Typography variant="body2" color="text.primary">
                        {truncateText(summary.summary)}
                      </Typography>
                      {summary.metadata && (
                        <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <Chip 
                            label={`${summary.metadata.total_sprints} Sprints`} 
                            size="small" 
                            color="primary" 
                            variant="outlined"
                          />
                          <Chip 
                            label={`${summary.metadata.total_prs} PRs`} 
                            size="small" 
                            color="secondary" 
                            variant="outlined"
                          />
                        </Box>
                      )}
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <IconButton
                        color="primary"
                        onClick={() => viewSummary(summary)}
                        sx={{ '&:hover': { bgcolor: '#e3f2fd' } }}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Container>

      {/* Summary Detail Modal */}
      <Dialog
        open={showModal}
        onClose={closeSummary}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { maxHeight: '80vh' }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            Sprint Summary Details
          </Typography>
          <IconButton onClick={closeSummary} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedSummary && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                Generated: {formatDateTime(selectedSummary.timestamp)}
              </Typography>
              
              {selectedSummary.metadata && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>Overview</Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                    <Chip 
                      label={`Total Sprints: ${selectedSummary.metadata.total_sprints}`} 
                      color="primary"
                    />
                    <Chip 
                      label={`Active Sprints: ${selectedSummary.metadata.active_sprints}`} 
                      color="success"
                    />
                    <Chip 
                      label={`Total PRs: ${selectedSummary.metadata.total_prs}`} 
                      color="secondary"
                    />
                    <Chip 
                      label={`Contributors: ${selectedSummary.metadata.total_github_contributors}`} 
                      color="info"
                    />
                  </Box>
                </Box>
              )}
              
              <Typography variant="h6" sx={{ mb: 2 }}>Detailed Summary</Typography>
              <Typography
                variant="body1"
                sx={{
                  whiteSpace: 'pre-wrap',
                  lineHeight: 1.6,
                  color: 'text.primary'
                }}
              >
                {selectedSummary.summary}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeSummary} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AiSprintRetro;